import * as React from "react";
import styled from "styled-components";
import { Button } from "react-bootstrap";
import { array2CsvBlob, downloadData } from "../../../lib/data";
import { useRef, useEffect, useState } from "react";
import { SideMenuWidthContext } from "../sidemenu";

type CrossTableElementProps = {
	sideMenuWidth: number;
	thirdRowTop?: string;
	topRowMinWidth?: number;
};
export const CrossTableElement = styled.table<CrossTableElementProps>`
	border-collapse: separate;
	border: 1px solid #aaa;
	th,
	td {
		padding: 3px;
	}
	th {
		border: 1px solid #aaa;
		text-align: center;
	}
	td {
		border: 1px dotted #aaa;
	}
	td {
		min-width: 70px;
	}
	tr.total-row {
		border-top: 1px solid #aaa;
		border-bottom: 1px solid #aaa;
		// background-color: #ddd;
		td {
			text-align: right;
		}
	}
	.plus {
		color: blue;
	}
	.minus {
		color: red;
	}
	.number {
		text-align: right;
	}
	td,
	th {
		border-right: 1px solid #aaa;
	}

	th.sticky-row-1,
	td.sticky-row-1 {
		${({ topRowMinWidth }) => (topRowMinWidth ? `min-width: ${topRowMinWidth}px;` : "")}
		position: sticky;
		top: 45px;
		background-color: lightgray;
		&:before {
			content: "";
			position: absolute;
			top: -2.5px;
			left: -2.5px;
			width: calc(100% + 5px);
			height: calc(100% + 5px);
			border: 2.5px solid white;
		}
	}

	th.sticky-row-2,
	td.sticky-row-2 {
		position: sticky;
		top: 73px;
		background-color: lightgray;
		&:before {
			content: "";
			position: absolute;
			top: -2.5px;
			left: -2.5px;
			width: calc(100% + 5px);
			height: calc(100% + 5px);
			border: 2.5px solid white;
		}
	}
	td.not-target {
		text-align: right !important;
	}

	th.sticky-row-3,
	td.sticky-row-3 {
		position: sticky;
		background-color: lightgray;
		&:before {
			content: "";
			position: absolute;
			top: -2.5px;
			left: -2.5px;
			width: calc(100% + 5px);
			height: calc(100% + 5px);
			border: 2.5px solid white;
		}
	}

	th.sticky-col-1,
	td.sticky-col-1 {
		position: sticky;
		left: ${({ sideMenuWidth }) => sideMenuWidth - 0.3}px;
		background-color: lightgray;
		&:before {
			content: "";
			position: absolute;
			top: -2.5px;
			left: -2.5px;
			width: calc(100% + 5px);
			height: calc(100% + 5px);
			border: 2.5px solid white;
		}
	}

	th.sticky-col-2,
	td.sticky-col-2 {
		position: sticky;
		left: ${({ sideMenuWidth }) => sideMenuWidth + 72.3}px;
		background-color: lightgray;
		&:before {
			content: "";
			position: absolute;
			top: -2.5px;
			left: -2.5px;
			width: calc(100% + 5px);
			height: calc(100% + 5px);
			border: 2.5px solid white;
		}
	}

	th.sticky-col-3,
	td.sticky-col-3 {
		position: sticky;
		left: ${({ sideMenuWidth }) => sideMenuWidth + 143.3}px;
		background-color: lightgray;
		&:before {
			content: "";
			position: absolute;
			top: -2.5px;
			left: -2.5px;
			width: calc(100% + 5px);
			height: calc(100% + 5px);
			border: 2.5px solid white;
		}
	}
	th.sticky-col-4,
	td.sticky-col-4 {
		position: sticky;
		left: ${({ sideMenuWidth }) => sideMenuWidth + 214.8}px;
		background-color: lightgray;
		&:before {
			content: "";
			position: absolute;
			top: -2.5px;
			left: -2.5px;
			width: calc(100% + 5px);
			height: calc(100% + 5px);
			border: 2.5px solid white;
		}
	}
	td.sticky-row-1.sticky-col-1 {
		z-index: 10;
		background-color: lightgray;
	}
`;

const ButtonAreaCaption = styled.caption`
	caption-side: top;
	text-align: left;
	margin-left: 10px;
`;

export type CrossCellData =
	| { value: string | number; rowSpan?: number; colSpan?: number; className?: string }
	| undefined;
export type CrossTableProps = {
	filename?: string;
	topRowMinWidth?: number;
	headers: CrossCellData[][];
	data: CrossCellData[][];
};

export const CrossTable = React.memo((props: CrossTableProps) => {
	const { headers, data, filename, topRowMinWidth } = props;
	const secondRowRef = useRef(null);
	const [secondRowHeight, setSecondRowHeight] = useState(0);
	const sideMenuWidth = React.useContext(SideMenuWidthContext);
	useEffect(() => {
		//console.log("useEffect called");
		if (secondRowRef.current) {
			const height = secondRowRef.current.getBoundingClientRect().height;
			setSecondRowHeight(height);
		}
	}, [headers]);

	const thirdRowTopValue = 74 + secondRowHeight;
	//console.log("thirdRowTopValue:", thirdRowTopValue);

	const onDownload = React.useCallback(() => {
		const tmpHeaders = headers.map((ds) =>
			ds.map((d) => (d && d.value !== null && d.value !== undefined ? d.value.toString() : "")),
		);
		const tmpData: string[][] = data.map((ds) =>
			ds.map((d) => {
				if (d && d.value !== null && d.value !== undefined) {
					return d.value.toString().replace(/%$/, "");
				}
				return "";
			}),
		);
		downloadData(array2CsvBlob([...tmpHeaders, ...tmpData]), `${filename || "cross-data"}.csv`);
	}, [data, headers, filename]);
	return (
		<>
			<CrossTableElement sideMenuWidth={sideMenuWidth} topRowMinWidth={topRowMinWidth}>
				<ButtonAreaCaption>
					<Button variant={"outline-secondary"} size={"sm"} onClick={onDownload}>
						Download
					</Button>
				</ButtonAreaCaption>
				{!!headers.length && (
					<thead>
						{headers.map((hs, row) => {
							return (
								<tr key={`header-${row}`} ref={row === 1 ? secondRowRef : null}>
									{hs.map((h, col) => {
										if (!h) return <React.Fragment key={`header-${row}-${col}`} />;
										const { value, ...attribute } = h;

										const additionalClassRow = row < 3 ? `sticky-row-${row + 1}` : "";
										const additionalClassCol = col < 4 ? `sticky-col-${col + 1}` : "";
										const style = row === 2 ? { top: `${thirdRowTopValue}px` } : {};

										return (
											<th
												key={`header-${row}-${col}`}
												{...attribute}
												className={`${additionalClassCol} ${additionalClassRow}`}
												style={style}
											>
												{value}
											</th>
										);
									})}
								</tr>
							);
						})}
					</thead>
				)}
				<tbody>
					{data.map((ds, row) => {
						const trClassName = ds.some((d) => d && d.value === "(n)") ? "total-row" : "data-row";
						return (
							<tr className={trClassName} key={`data-${row}`} ref={row === 1 ? secondRowRef : null}>
								{ds.map((d, col) => {
									if (!d) return <React.Fragment key={`header-${row}-${col}`} />;
									const { value, className, ...attribute } = d;
									const classNames: string[] = ["value"];
									if (className) classNames.push(className);
									if (value) {
										if (/^\+/.test(value.toString())) classNames.push("plus");
										if (/^-/.test(value.toString())) classNames.push("minus");
										if (/^[-+]?(\d|\.)+[%|％]?$/.test(value.toString())) classNames.push("number");
									}
									const additionalClassRow = row < 3 ? `sticky-row-${row + 1}` : "";
									const additionalClassCol = col < 4 ? `sticky-col-${col + 1}` : "";
									const style = row === 2 ? { top: `${thirdRowTopValue}px` } : {};
									return (
										<td
											key={`data-${row}-${col}`}
											{...attribute}
											className={`${additionalClassCol} ${additionalClassRow} ${classNames.join(" ")}`}
											style={style}
										>
											{value || ""}
										</td>
									);
								})}
							</tr>
						);
					})}
				</tbody>
			</CrossTableElement>
		</>
	);
});
